// src/frontend/data/books/DarkForest/data/cap10.js

const CAP10 = {
  id: 10,
  title: "¿Cómo usar tu billetera?",
  sections: [
    {
      heading: "El verdadero desafío comienza aquí",
      text: [
        "Una vez que ya creaste tu billetera y la respaldaste correctamente, llega el siguiente reto: usarla de forma segura en el mundo real.",
        "Si eres una persona que no mueve activos con frecuencia, o si casi no interactúas con contratos inteligentes de DeFi, NFT, GameFi o cualquier aplicación Web3 moderna, tus activos tienden a mantenerse relativamente seguros.",
        "El mayor riesgo comienza en el momento en que empiezas a firmar transacciones, conectar tu billetera a sitios desconocidos, aprobar permisos o mover fondos entre redes y protocolos."
      ]
    },

    /* =============================== */
    /* SECCIÓN 1 - USO BÁSICO         */
    /* =============================== */

    {
      heading: "Uso básico de la billetera",
      text: [
        "El uso cotidiano de una billetera cripto suele incluir tres acciones fundamentales:",
        "🔹 Recibir fondos.",
        "🔹 Enviar fondos.",
        "🔹 Firmar transacciones.",
        "Aunque parecen acciones simples, cada una de ellas conlleva riesgos si no se realizan con cuidado."
      ],
      subsections: [
        {
          heading: "Recibir fondos",
          text: [
            "Recibir fondos es la operación más segura, ya que no requieres firmar nada. Sin embargo, debes:",
            "🔹 Verificar siempre que la red utilizada corresponde a la de tu billetera.",
            "🔹 Revisar dos veces la dirección antes de compartirla.",
            "🔹 Evitar copiar direcciones desde dispositivos que puedan estar infectados con malware.",
            "Recuerda que muchas billeteras permiten generar nuevas direcciones sin afectar la seed. Esto es normal."
          ]
        },
        {
          heading: "Enviar fondos",
          text: [
            "Enviar activos es una operación delicada porque una vez firmada la transacción y enviada a la red, no existe devolución.",
            "Buenas prácticas:",
            "🔹 Enviar primero una transacción pequeña de prueba si estás enviando grandes cantidades.",
            "🔹 Verificar cuidadosamente cada carácter de la dirección de destino.",
            "🔹 Confirmar la red, el gas y el importe.",
            "Cualquier error aquí puede resultar en pérdidas irreversibles."
          ]
        },
        {
          heading: "Firmar transacciones",
          text: [
            "Firmar transacciones es el corazón de cualquier interacción con Web3. Cuando firmas, autorizas algo en la cadena de bloques.",
            "Firmar implica riesgos, porque muchas solicitudes de firma contienen permisos ocultos o maliciosos.",
            "Es fundamental leer siempre lo que se está firmando, incluso si parece tedioso."
          ]
        }
      ]
    },

    /* =============================== */
    /* SECCIÓN 2 - INTERACCIÓN WEB3   */
    /* =============================== */

    {
      heading: "Interactuando con Web3 (DeFi, NFT, GameFi y más)",
      text: [
        "La Web3 moderna es un ecosistema inmenso: protocolos DeFi, marketplaces NFT, juegos GameFi, puentes cross-chain, bóvedas de rendimiento, herramientas sociales descentralizadas, etc.",
        "Cada uno de estos entornos tiene riesgos específicos, pero todos tienen un patrón común: requieren que conectes tu billetera y firmes acciones."
      ],
      subsections: [
        {
          heading: "Conexión de la billetera",
          text: [
            "Conectar tu billetera a un sitio Web3 no debería ser algo rutinario o automático.",
            "Antes de conectar:",
            "🔹 Asegúrate de estar en el sitio oficial.",
            "🔹 Revisa que el dominio no tenga errores ortográficos o sustituciones (phishing).",
            "🔹 Usa marcadores para sitios confiables.",
            "Conectar la billetera, por sí solo, no transfiere fondos, pero abre la puerta a solicitudes de firma posteriores."
          ]
        },
        {
          heading: "Firmar permisos (Approvals)",
          text: [
            "En muchos protocolos DeFi o contratos NFT, firmar no solo autoriza una transacción puntual, sino que otorga permisos sobre tus tokens.",
            "Esto se conoce como *approval*.",
            "Riesgos:",
            "🔹 Un approval malicioso puede permitir que un atacante gaste tus tokens sin tu permiso.",
            "🔹 Incluso protocolos legítimos pueden tener vulnerabilidades.",
            "Recomendaciones:",
            "🔹 Revisa frecuentemente tus approvals en herramientas como revoke.cash.",
            "🔹 Usa approvals limitados cuando sea posible."
          ]
        },
        {
          heading: "Proyectos de alto riesgo",
          text: [
            "Cualquier interacción que prometa altos rendimientos, recompensas inmediatas o funciones poco claras debe verse con extrema cautela.",
            "Ejemplos típicos de riesgo:",
            "🔹 Sitios clonados.",
            "🔹 Contratos mal auditados.",
            "🔹 Airdrops falsos que te piden firmar cosas.",
            "🔹 DApps nuevas sin reputación ni auditorías."
          ]
        }
      ]
    },

    /* =============================== */
    /* SECCIÓN 3 - HIGIENE DIGITAL    */
    /* =============================== */

    {
      heading: "Higiene digital al usar tu billetera",
      text: [
        "Tu billetera puede ser segura, pero tu entorno digital no necesariamente lo es. Una gran parte de los robos ocurre por descuidos del usuario, no por fallos criptográficos.",
        "Mantener buena higiene digital es tan importante como elegir una buena billetera."
      ],
      subsections: [
        {
          heading: "Dispositivos seguros",
          text: [
            "Procura mantener tu dispositivo actualizado y libre de malware.",
            "Evita instalar extensiones desconocidas en el navegador.",
            "Mantén un navegador exclusivo para Web3 si interactúas frecuentemente.",
            "Desconfía de links enviados por desconocidos o por mensajería."
          ]
        },
        {
          heading: "Evitar redes inseguras",
          text: [
            "Nunca firmes transacciones desde redes WiFi públicas o desconocidas.",
            "Si es estrictamente necesario, usa una VPN de alta reputación.",
            "Deshabilita conexiones automáticas a redes abiertas."
          ]
        },
        {
          heading: "Separación de billeteras",
          text: [
            "Separar tus billeteras por niveles de riesgo es una excelente práctica:",
            "🔹 Una billetera fría para almacenar ahorros a largo plazo.",
            "🔹 Una billetera caliente para uso diario.",
            "🔹 Una billetera específicamente para interactuar con contratos dudosos o para pruebas.",
            "Así, si una de ellas se compromete, no pierdes todo."
          ]
        }
      ]
    },

    /* =============================== */
    /* SECCIÓN 4 - SEÑALES DE ALERTA  */
    /* =============================== */

    {
      heading: "Señales de alerta al usar tu billetera",
      text: [
        "Aprender a detectar señales de riesgo es clave para sobrevivir en el ecosistema Web3.",
        "Si observas cualquiera de estos síntomas, detente inmediatamente:"
      ],
      list: [
        "Un sitio te pide tu seed phrase (NUNCA la compartas).",
        "Un contrato solicita permisos ilimitados sin razón clara.",
        "La DApp te pide firmar una transacción que aparece vacía o con datos extraños.",
        "El dominio tiene errores ortográficos o símbolos raros.",
        "El sitio web no tiene historial, comunidad o documentación verificable.",
        "La firma aparece en un formato que tu billetera no reconoce."
      ]
    },

    /* =============================== */
    /* SECCIÓN 5 - CONCLUSIÓN         */
    /* =============================== */

    {
      heading: "Conclusión: usar la billetera es donde ocurre el verdadero riesgo",
      text: [
        "Crear y respaldar una billetera es relativamente sencillo. Lo difícil viene después: usarla en un entorno lleno de contratos inteligentes, permisos invisibles y sitios potencialmente peligrosos.",
        "Cuanta más experiencia ganes, más natural será revisar permisos, validar dominios y desconfiar de cualquier cosa que parezca sospechosa.",
        "El objetivo no es vivir con miedo, sino navegar Web3 con claridad, criterio y buenas prácticas.",
        "Si mantienes disciplina y sentido común, podrás aprovechar la Web3 sin convertirte en una víctima más."
      ]
    }
  ]
};

export default CAP10;
